import { Content, Header } from 'antd/lib/layout/layout';
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  createView,
  deleteSpace,
  getWorkspaceById,
  leaveSpace,
  updateSpaceSettings,
  UpdateSpaceSettingsModel,
} from '../../api/workspace';
import Workspace, { WorkspaceFramework } from '../../types/Workspace';
import { getSpaceDefaultRoute } from '../../utils/workspaceUtils';

import { PlusOutlined, ProjectOutlined, SettingOutlined, TableOutlined, UserOutlined } from '@ant-design/icons';
import { MenuProps, message, Modal } from 'antd';
import { AxiosError } from 'axios';
import { LockIcon } from '../../assets/CustomIcons/LockIcon';
import { ManageViews } from '../../assets/CustomIcons/ManageViews';
import { useGetUserFromAccessToken } from '../../common/auth';
import { capitalizeFirstLetter } from '../../common/utils';
import { getWorkspaceViewGroup } from '../../common/viewUtils';
import { getSpaceScreen } from './spaceScreen';
import { analytics } from '../../analytics/analytics';
import { AnalyticsEvent } from '../../analytics/analyticsEvent';
import Backlog from '../../components/Backlog/Backlog';
import Board from '../../components/Board/Board';
import { ManageViewsModal } from '../../components/Modal/ManageViewsModal';
import { NewSpaceViewModal } from '../../components/Modal/NewSpaceViewModal';
import { mergeServerViews } from '../../components/TaskManagement/helpers/viewHelpers';
import PlanningSession from '../../components/Planning/PlanningSession';
import { BaseButton } from '../../components/UI/BaseButton';
import CustomTooltip from '../../components/UI/Tooltip/CustomTooltip';
import { useSpaceSocket } from '../../hooks/useSpaceSocket';
import { queryClient } from '../../queryClient';
import RoutePaths from '../../router/RoutePaths';
import { updateSpaceQuery } from '../../serverState/helpers/queryHelper';
import { useGetOrganizationInvites } from '../../serverState/organization';
import { useTaskByNumber } from '../../serverState/tasks';
import { withoutConsumedInvites } from './pendingInvites';
import {
  useGetActualSpaceId,
  useGetUsersWithWorkspaceAccess,
  useGetWorkspace,
  useWorkspaceStore,
} from '../../serverState/workspace';
import { WorkspaceAccessRole } from '../../types/WorkspaceAccess';
import { resolveWorkspaceRole } from '../../utils/resolveWorkspaceRole';
import {
  WorkspaceView,
  WorkspaceViewEntityType,
  WorkspaceViewGroup,
  WorkspaceViewRepresentation,
} from '../../types/WorkspaceView';
import { ViewColumn, WorkspaceViewColumn } from '../../types/WorkspaceViewColumn';
import { WorkspaceViewSort } from '../../types/WorkspaceViewSortBy';
import { SideBarItemEnum } from '../BaseIndex/helpers/SideBarItems';
import { TaskView } from '../Task/TaskView';
import { ViewContext } from './Context/ViewContext';
import './Space.scss';
import { SpaceMembersModal } from './SpaceMembersModal/SpaceMembersModal';
import { SpaceSettingsModal, SpaceSettingsTab } from './SpaceSettingsModal/SpaceSettingsModal';
import { useSpaceSettingsStore } from '../../stores/spaceSettingsStore';
import { SpaceStatusContent, SpaceStatus } from './SpaceStatusContent';
import { SpaceViewsHeader } from './SpaceViewsHeader';
import { isSpaceForRoute } from './spaceIdentity';
import { MCP_URL } from '../Analytics/MCPConnectionSection';

const Analytics = lazy(() => import('../Analytics/Analytics'));

export default function Space() {
  const { orgId, spaceId, taskId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isAnalyticsRoute = location.pathname.includes(`/${SideBarItemEnum.analytics}`);

  const actualSpaceId = useGetActualSpaceId();
  const usersWithAccessToSpaceQuery = useGetUsersWithWorkspaceAccess(Number(actualSpaceId));
  const organisationInvitesQuery = useGetOrganizationInvites();
  const workspaceQuery = useGetWorkspace();
  const workspaceId = workspaceQuery.data?.id;

  // Kick the task fetch off on the first render. This component returns null until
  // `space` resolves (`if (!space) return null`, line 473), so without this the request
  // would queue behind the workspace lookup instead of running next to it. React Query
  // dedupes it against the identical call inside useTaskDetails — same public id from the
  // URL, same key — whenever that observer mounts while this fetch is still in flight.
  //
  // `refetchOnMount: false` opts this observer out of forcing a revalidation of its own.
  // It still runs the initial load (nothing is cached yet), so a cold open is unchanged and
  // costs USUALLY two requests: if this fetch is still in flight when the workspace lookup
  // un-gates the render below, Task View's observer joins the in-flight promise (query-core
  // hands it the existing retryer while `data === undefined`) and it is one; if this fetch
  // has already resolved, its `refetchOnMount: 'always'` revalidates and it is two. Which
  // way it lands is a race between two requests we do not control, and closing it would mean
  // weakening the staleTime:0 + refetchOnMount:'always' guarantee on the observer that
  // actually renders the record. What the opt-out does remove is the extra request on a WARM
  // open (the same task reopened while its record is still in cache), where this observer
  // would otherwise refetch on top of Task View's.
  //
  // When taskId is absent this registers a disabled ['taskDetail', spaceId, 0] entry
  // that never fetches — harmless, and cheaper than a conditional hook. (#3010)
  useTaskByNumber(spaceId, taskId ? Number(taskId) : undefined, { refetchOnMount: false });

  // // TODO: implement if visibility is public not to check for workspace access
  const { setWorkspace } = useWorkspaceStore();

  const [spaceStatus, setSpaceStatus] = useState<SpaceStatus>(SpaceStatus.Loading);

  const currentUserQuery = useGetUserFromAccessToken();
  const loggedUser = currentUserQuery.data?.user;

  const [userWorkspaceRole, setUserWorkspaceRole] = useState<WorkspaceAccessRole>();

  const [space, setSpace] = React.useState<Workspace>();
  // Tooltip fires only when the space name is actually clipped (#2941 bounce-back),
  // measured on hover rather than assumed, matching the pattern in NotificationCard.tsx.
  const [isSpaceNameTruncated, setIsSpaceNameTruncated] = useState(false);
  const spaceNameRef = useRef<HTMLDivElement>(null);

  const [headerItems, setHeaderItems] = useState<MenuProps['items']>([]);
  // Filled by the views sync below once this space has loaded. Not seeded from
  // workspaceQuery: that query follows the persisted currentWorkspace, which can still be
  // the previously open space.
  const [views, setViews] = useState<WorkspaceView[]>([]);
  const [selectedView, setSelectedView] = useState<WorkspaceView | null>(null);
  // Set by ViewDisplayDropdown when the user edits this view's display config without
  // saving. Gates whether the active view may adopt a peer's saved change (see below).
  const [isViewDirty, setIsViewDirty] = useState<boolean>(false);

  // Switching spaces in the sidebar keeps this component mounted (same route element), so
  // everything above still describes the previous space. Drop it during render, before the
  // new URL ever commits, so the old space's view is never painted under the new URL and the
  // view selection below never picks from the old list. (DEV-3247)
  const [stateSpaceId, setStateSpaceId] = useState(spaceId);
  if (stateSpaceId !== spaceId) {
    setStateSpaceId(spaceId);
    setSpace(undefined);
    setSpaceStatus(SpaceStatus.Loading);
    setViews([]);
    setSelectedView(null);
    setIsViewDirty(false);
  }
  // True only once `space` is the space the URL names. The previous space's state, or a
  // workspace query still keyed on it, must not drive what renders here.
  const isCurrentSpace = isSpaceForRoute(space, spaceId);

  const [isNewViewOpen, setIsNewViewOpen] = useState<boolean>(false);
  const [isManageViewsOpen, setIsManageViewsOpen] = useState<boolean>(false);

  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (!usersWithAccessToSpaceQuery.data) return;

    setUserWorkspaceRole(resolveWorkspaceRole(usersWithAccessToSpaceQuery.data, loggedUser?.id));
  }, [usersWithAccessToSpaceQuery.data, usersWithAccessToSpaceQuery.status, loggedUser?.id, actualSpaceId]);

  useEffect(() => {
    // A space left before its lookup returned must not overwrite the one now open.
    let isStale = false;
    if (spaceId) {
      getWorkspaceById(spaceId)
        .then((res) => {
          if (isStale) return;
          setWorkspace(res.data.id.toString());
          setSpaceStatus(SpaceStatus.Loaded);
          setSpace(res.data);
          updateSpaceQuery(res.data);
        })
        .catch((e: AxiosError) => {
          if (isStale) return;
          if (e.response?.status === 403) {
            setSpaceStatus(SpaceStatus.Forbidden);
          } else if (e.response?.status === 404) {
            setSpaceStatus(SpaceStatus.NotFound);
          }
        });
    }
    return () => {
      isStale = true;
    };
  }, [spaceId]);

  // The server views the local list was last synced from, so the next sync can tell which
  // local views hold unsaved edits (see mergeServerViews).
  const syncedServerViewsRef = useRef<WorkspaceView[]>([]);
  const syncedWorkspaceIdRef = useRef<number>();
  useEffect(() => {
    // workspaceQuery follows the persisted currentWorkspace, which lags the URL on a space
    // switch. Only this space's views may land in the list.
    if (!space || !isCurrentSpace || workspaceQuery.data?.id !== space.id) return;
    // First sync for this space: nothing local to preserve from another space's views.
    if (syncedWorkspaceIdRef.current !== space.id) {
      syncedWorkspaceIdRef.current = space.id;
      syncedServerViewsRef.current = [];
    }
    const uniqueViews = (workspaceQuery.data?.Views || []).filter(
      (view, index, self) => index === self.findIndex((v) => v.id === view.id)
    );
    const previousServerViews = syncedServerViewsRef.current;
    syncedServerViewsRef.current = uniqueViews;
    const framework = workspaceQuery.data?.framework || WorkspaceFramework.scrum;
    setViews((localViews) => mergeServerViews(framework, uniqueViews, previousServerViews, localViews));
  }, [workspaceQuery.data?.Views, workspaceQuery.data?.id, space?.id, isCurrentSpace]);

  // Redirect bare space URLs (no module segment) to the default module
  useEffect(() => {
    if (!space || !orgId) return;

    const moduleSegments = [
      SideBarItemEnum.triage,
      SideBarItemEnum.epics,
      SideBarItemEnum.backlog,
      SideBarItemEnum.planning,
      SideBarItemEnum.sprint,
      SideBarItemEnum.kanban,
      SideBarItemEnum.analytics,
      SideBarItemEnum.archive,
      SideBarItemEnum.retrospectives,
      SideBarItemEnum.reports,
    ];
    const hasModule = moduleSegments.some((seg) => location.pathname.includes(seg));

    if (!hasModule && !taskId) {
      const route = getSpaceDefaultRoute(orgId, space);
      if (route) navigate(route, { replace: true });
    }
  }, [space, orgId, location.pathname, taskId, navigate]);

  // Use socket with conditional spaceId - hook will handle undefined values
  useSpaceSocket(actualSpaceId || workspaceId?.toString());

  useEffect(() => {
    if (isAnalyticsRoute) return;
    let mappedViews: WorkspaceView[] = [];
    if (location.pathname.includes(SideBarItemEnum.triage)) {
      mappedViews = views.filter((view) => view.group === WorkspaceViewGroup.triage) || [];
    } else if (location.pathname.includes(SideBarItemEnum.archive)) {
      mappedViews = views.filter((view) => view.group === WorkspaceViewGroup.archive) || [];
    } else if (location.pathname.includes(SideBarItemEnum.backlog)) {
      mappedViews = views.filter((view) => view.group === WorkspaceViewGroup.backlog) || [];
    } else if (location.pathname.includes(SideBarItemEnum.epics)) {
      mappedViews = views.filter((view) => view.group === WorkspaceViewGroup.epics) || [];
    } else if (location.pathname.includes(SideBarItemEnum.planning)) {
      mappedViews = views.filter((view) => view.group === WorkspaceViewGroup.planning) || [];
    } else if (location.pathname.includes(SideBarItemEnum.sprint)) {
      mappedViews = views.filter((view) => view.group === WorkspaceViewGroup.sprint) || [];
    } else if (location.pathname.includes(SideBarItemEnum.kanban)) {
      mappedViews = views.filter((view) => view.group === WorkspaceViewGroup.kanban) || [];
    }

    setHeaderItems([
      ...(mappedViews
        .sort((a, b) => (a.orderIndex ?? a.id) - (b.orderIndex ?? b.id))
        .map((view) => ({
          label: (
            <div
              className="flex items-center gap-1 h-8 mb-1 px-2 rounded hover:bg-neutral-200"
              data-menu-item={view.id}
            >
              {view.representation === WorkspaceViewRepresentation.table ? (
                <TableOutlined className="text-neutral-900" />
              ) : (
                <ProjectOutlined className="text-neutral-900" />
              )}
              <CustomTooltip title={view.name.length > 19 ? view.name : ''} placement="bottom">
                <div className="max-w-[140px] text-neutral-900 font-normal text-sm truncate">{view.name}</div>
              </CustomTooltip>
            </div>
          ),
          key: view.id.toString(),
        })) || []),
      {
        label: (
          <div className="flex items-center gap-1 h-8 mb-1 px-2 rounded hover:bg-neutral-200">
            <PlusOutlined />
          </div>
        ),
        key: 'new_view',
      },
    ]);

    // Nothing to select until this space and its views have loaded. Picking now would choose
    // from an empty or foreign list and could keep the previous space's view. (DEV-3247)
    if (!isCurrentSpace) return;

    const urlSelection = location.pathname.split('/');

    const viewParam = searchParams.get('view');

    if (
      urlSelection.length > 2 &&
      urlSelection[2] === 'space' &&
      viewParam &&
      mappedViews.find((view) => view.publicId === viewParam)
    ) {
      const freshView = mappedViews.find((view) => view.publicId === viewParam);
      let view = freshView;
      // Switching views drops any unsaved display edits (#2364), so the new one starts clean.
      if (selectedView && selectedView.publicId !== viewParam && isViewDirty) {
        setIsViewDirty(false);
      }
      // Don't use old selectedView reference during modal operations (prevents snap-back during drag).
      if (selectedView && selectedView.publicId === viewParam && !isManageViewsOpen) {
        // With unsaved local display edits, keep them and take only the filters another user
        // pushed via viewUpdated (#2568) — otherwise the Filters dot/Reset stays stuck on stale
        // local filters. With no local edits there is nothing to protect, so adopt the server's
        // view wholesale: a peer's saved columns/groupBy/sortBy used to sit in the workspace
        // cache unseen until you switched views or reloaded.
        if (freshView) {
          view = isViewDirty ? { ...selectedView, filters: freshView.filters } : freshView;
        } else {
          view = selectedView;
        }
      }
      setSelectedView(view || null);
    } else {
      const defaultView = mappedViews.find((view) => view.default) ?? mappedViews[0];
      if (defaultView) {
        searchParams.set('view', defaultView.publicId);
        navigate(`${location.pathname}?${searchParams.toString()}`);
        setSelectedView(defaultView);
        setIsViewDirty(false);
      }
    }
  }, [space, views, location.pathname, searchParams, isManageViewsOpen, isViewDirty, isCurrentSpace]);

  // Track screen opened event, once per (space, sidebar item, saved view). Waits until the view
  // is resolved, so landing on a bare module URL (redirected to ?view=<default>) sends one event
  // carrying that view. spaceId/organizationId come from the space in the URL: the workspace
  // analytics.track enriches from lags it right after a space switch. (#3248)
  const lastScreenEventKeyRef = useRef<string | null>(null);
  const screen = isAnalyticsRoute ? null : getSpaceScreen(location.pathname);
  const viewParamForScreen = searchParams.get('view');
  useEffect(() => {
    if (!screen) {
      // Leaving the sidebar item (e.g. into a task) means coming back is a new screen open.
      lastScreenEventKeyRef.current = null;
      return;
    }
    if (!space || !isCurrentSpace) return;

    const group = getWorkspaceViewGroup(location.pathname);
    let view: string | null;
    if (selectedView && selectedView.group === group && selectedView.publicId === viewParamForScreen) {
      view = selectedView.publicId;
    } else if (
      workspaceQuery.data?.id === space.id &&
      !(workspaceQuery.data.Views || []).some((serverView) => serverView.group === group)
    ) {
      view = null;
    } else {
      return; // The view selection above has not settled yet.
    }

    const key = `${space.id}|${screen}|${view}`;
    if (lastScreenEventKeyRef.current === key) return;
    lastScreenEventKeyRef.current = key;
    analytics.track(AnalyticsEvent.screenOpened, {
      screen,
      view,
      spaceId: space.id,
      organizationId: space.organizationId,
    });
  }, [screen, space, isCurrentSpace, selectedView, viewParamForScreen, workspaceQuery.data, location.pathname]);

  const onClickHeaderMenu: MenuProps['onClick'] = (e) => {
    if (e.key === 'new_view') {
      setIsNewViewOpen(true);
      return;
    }
    const view = views.find((view) => view.id === Number(e.key)) || null;
    setSelectedView(view);
    navigate(`${location.pathname}?view=${view?.publicId}`);
  };

  const [openSpaceSettings, setOpenSpaceSettings] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<SpaceSettingsTab>(SpaceSettingsTab.GENERAL);

  // Open Space Settings on a requested tab when triggered from elsewhere in the
  // space (e.g. "Edit statuses" / "Manage labels" in the task table dropdowns).
  const requestedSettingsTab = useSpaceSettingsStore((state) => state.requestedTab);
  const clearSettingsRequest = useSpaceSettingsStore((state) => state.clearRequest);
  useEffect(() => {
    if (requestedSettingsTab) {
      setSelectedTab(requestedSettingsTab);
      setOpenSpaceSettings(true);
      clearSettingsRequest();
    }
  }, [requestedSettingsTab, clearSettingsRequest]);

  const [confirmLoadingDeleteSpace, setConfirmLoadingDeleteSpace] = useState<boolean>(false);
  const [openDeleteSpace, setOpenDeleteSpace] = useState<boolean>(false);
  const [openEditSpaceMembers, setOpenSpaceMembers] = useState<boolean>(false);
  const [openLeaveSpace, setOpenLeaveSpace] = useState<boolean>(false);
  const [confirmLoadingLeaveSpace, setConfirmLoadingLeaveSpace] = useState<boolean>(false);

  const handleConfirmDeleteSpace = () => {
    setConfirmLoadingDeleteSpace(true);
    if (workspaceId) {
      deleteSpace(workspaceId)
        .then(() => {
          setConfirmLoadingDeleteSpace(false);
          setOpenDeleteSpace(false);
          setOpenSpaceSettings(false);
          // TODO: query client invalidate workspaces for org
          queryClient.invalidateQueries({
            queryKey: ['workspaces', space?.organizationId],
          });

          void messageApi.success('Space deleted');
          navigate(RoutePaths.Home);
          window.location.reload();
        })
        .catch((e) => {
          console.log(e);
          void messageApi.error('Failed to delete space');
        });
    } else {
      setConfirmLoadingDeleteSpace(false);
      setOpenDeleteSpace(false);
      void messageApi.error('Failed to delete space');
    }
  };

  const handleCancelSpaceDelete = () => {
    setOpenDeleteSpace(false);
  };

  const handleConfirmLeaveSpace = () => {
    setConfirmLoadingLeaveSpace(true);
    if (workspaceId) {
      leaveSpace(Number(workspaceId))
        .then(() => {
          setConfirmLoadingLeaveSpace(false);
          setOpenLeaveSpace(false);
          setOpenSpaceSettings(false);

          queryClient.invalidateQueries({
            queryKey: ['workspaces', space?.organizationId],
          });

          void messageApi.success('Space left');
          navigate(RoutePaths.Home);
          window.location.reload();
        })
        .catch((error) => {
          console.log(error);

          setConfirmLoadingLeaveSpace(false);
          setOpenLeaveSpace(false);
          void messageApi.error(error.response.data);
        });
    } else {
      setConfirmLoadingLeaveSpace(false);
      setOpenLeaveSpace(false);
      void messageApi.error('Failed to leave space');
    }
  };

  const handleCancelSpaceLeave = () => {
    setOpenLeaveSpace(false);
  };

  const handleConfirmSaveUpdate = async (updateModel: UpdateSpaceSettingsModel) => {
    if (!workspaceId) return;
    try {
      const res = await updateSpaceSettings(workspaceId, updateModel);

      if (res.status === 200) {
        void messageApi.success('Space settings updated');
        await queryClient.invalidateQueries({ queryKey: ['workspace', space?.id] });
        await queryClient.invalidateQueries({ queryKey: ['workspaces'] });
        // If updated framework trigger update of whole space
        if (updateModel.framework && space?.framework !== updateModel.framework) {
          const route = getSpaceDefaultRoute(orgId, res.data);
          if (route) navigate(route);
        }
        setSpace(res.data);
      }
    } catch (e) {
      console.log('form is not valid');
    }
  };

  const handleCancelSpaceSettings = () => {
    setOpenSpaceSettings(false);
  };

  const handleCreateNewView = async (
    name: string,
    representation: WorkspaceViewRepresentation,
    entityType: WorkspaceViewEntityType,
    columns: ViewColumn[],
    groupBy: string,
    sortBy: WorkspaceViewSort
  ) => {
    if (!space) {
      return;
    }

    const group: WorkspaceViewGroup = getWorkspaceViewGroup(location.pathname);
    const serverColumns: WorkspaceViewColumn = {
      hidden: columns.filter((column) => !column.selected).map((column) => column.id),
      order: columns.map((column) => column.id),
    };
    const newView = await createView(space.id, name, group, representation, entityType, groupBy, serverColumns, sortBy);

    queryClient.setQueryData(['workspace', workspaceId], (oldData: Workspace) => {
      return {
        ...oldData,
        Views: [...oldData.Views.filter((view) => view.id !== newView.data.id), newView.data],
      };
    });

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('view', newView.data.publicId.toString());

    navigate(`${location.pathname}?${newSearchParams.toString()}`);
    setIsNewViewOpen(false);
  };

  // Public spaces are opt-in now, so a pending org invite no longer implies membership of every
  // public space. Count only invites explicitly tied to this space (same as private).
  // ...and an invitation that the member list has already superseded is not a pending invite at
  // all, so counting it here inflated the header count next to the duplicate row. (#2988)
  const numOfInvitedUsers =
    withoutConsumedInvites(organisationInvitesQuery.data ?? [], usersWithAccessToSpaceQuery.data ?? []).filter(
      (invite) => {
        const inviteSpace = invite.Workspaces.find((invitationSpace) => invitationSpace.workspaceId === space?.id);
        return !!inviteSpace;
      }
    ).length || 0;

  // The view the content area may render: only one that belongs to the space in the URL.
  const activeView = isCurrentSpace ? selectedView : null;

  // Space Settings opens from inside a task view too ("Manage Statuses" / "Manage Labels"
  // in its property dropdowns), so these modals render in both branches below. With them
  // only in the space branch, a request made from a task view still opened the modal's
  // state but nothing was mounted to show it until a later route rendered that branch.
  // Both branches return the same fragment shape, contextHolder / content / these modals,
  // so the modals keep their place in the tree, and their unsaved state, when the route
  // switches between a task and a module. #3259
  const spaceSettingsModals = (
    <>
      <Modal
        title="Delete this Space?"
        open={openDeleteSpace}
        onOk={handleConfirmDeleteSpace}
        confirmLoading={confirmLoadingDeleteSpace}
        onCancel={handleCancelSpaceDelete}
        okButtonProps={{ danger: true }}
        centered
      >
        <p>Please confirm that you want to delete this Space? This action is not reversible.</p>
      </Modal>

      <SpaceSettingsModal
        isOpen={openSpaceSettings}
        onCancel={handleCancelSpaceSettings}
        space={space}
        userWorkspaceRole={userWorkspaceRole}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        handleConfirmSaveUpdate={handleConfirmSaveUpdate}
        handleLeaveSpace={() => setOpenLeaveSpace(true)}
        handleDeleteSpace={() => setOpenDeleteSpace(true)}
      />

      <Modal
        title="Leave this Space?"
        open={openLeaveSpace}
        onOk={handleConfirmLeaveSpace}
        confirmLoading={confirmLoadingLeaveSpace}
        onCancel={handleCancelSpaceLeave}
        okButtonProps={{ danger: true }}
        centered
      >
        <p>Please confirm that you want to leave this Space?</p>
      </Modal>
    </>
  );

  if (taskId) {
    // Don't fall through to the full space chrome below while `space` is still
    // loading — painting that chrome and then immediately replacing it with
    // TaskView once `space` resolves is what caused the visible blink when
    // opening a task view from a notification click.
    if (!space || !isCurrentSpace) return null;

    const defaultView =
      selectedView ??
      views.find((view) => view.group === WorkspaceViewGroup.backlog && view.default) ??
      views.find((view) => view.group === WorkspaceViewGroup.backlog) ??
      views.find((view) => view.default) ??
      views[0];
    // On a full page load `space` resolves one render before the views sync fills `views`,
    // and TaskView cannot render without a view. Wait for it, as for `space` above. (#3260)
    if (!defaultView) return null;
    return (
      <>
        {contextHolder}
        <TaskView view={defaultView} />
        {spaceSettingsModals}
      </>
    );
  }

  return (
    <>
      {contextHolder}
      {/* Fills the layout column instead of letting content push past the viewport.
          The list below sizes itself by flex from here, so no level hard-codes how
          tall the chrome above it is. #2357 */}
      <div className="flex h-full min-h-0 flex-col">
        <Header
          className="header-section"
          style={{
            padding: 0,
            // paddingLeft: '0.5rem',
            paddingRight: '2.5rem',

            background: 'var(--color-brand-white)',
            borderBottom: '1px solid var(--color-neutral-300)',
            display: 'flex',
            height: 'initial',
          }}
        >
          <div className={'flex-grow pl-8'}>
            <div className="flex items-center justify-between gap-2 pt-2 pb-0.5">
              <div className="flex items-center flex-grow">
                {space?.visibility === 'private' && <LockIcon className="text-neutral-900" />}
                {space?.visibility === 'public' && <div className="text-neutral-900 text-xl font-semibold">#</div>}
                <div className="flex gap-1.5 text-brand-grey-dark text-xl font-semibold px-1.5 rounded">
                  <CustomTooltip title={isSpaceNameTruncated ? (space?.name ?? '') : ''} placement="bottom">
                    <div
                      ref={spaceNameRef}
                      className="max-w-[502px] truncate"
                      onMouseEnter={() => {
                        if (spaceNameRef.current) {
                          setIsSpaceNameTruncated(spaceNameRef.current.scrollWidth > spaceNameRef.current.clientWidth);
                        }
                      }}
                    >
                      {space?.name}
                    </div>
                  </CustomTooltip>
                  <div className="justify-start text-neutral-1000 text-xl font-normal">
                    {`/ ${isAnalyticsRoute ? 'Analytics' : capitalizeFirstLetter(selectedView?.group ?? '')}`}
                  </div>
                </div>
              </div>
              <div className={'flex items-center gap-2 min-w-fit'}>
                <BaseButton type="secondary" size="medium" onClick={() => setOpenSpaceMembers(true)}>
                  <UserOutlined />
                  {usersWithAccessToSpaceQuery.isLoading
                    ? 'Members'
                    : `Members | ${(usersWithAccessToSpaceQuery.data?.length ?? 0) + numOfInvitedUsers}`}
                </BaseButton>

                <BaseButton type="secondary" size="medium" onClick={() => setOpenSpaceSettings(true)}>
                  <SettingOutlined />
                  Settings
                </BaseButton>
              </div>
            </div>
            {!isAnalyticsRoute && (
              <div className="flex items-center justify-between mt-3">
                <SpaceViewsHeader
                  headerItems={headerItems}
                  selectedView={selectedView ?? undefined}
                  onClickHeaderMenu={onClickHeaderMenu}
                ></SpaceViewsHeader>
                <div className="w-[138px]">
                  <div className="mb-1">
                    <BaseButton type="text" size="medium" onClick={() => setIsManageViewsOpen(true)}>
                      <ManageViews />
                      <span className="whitespace-nowrap">Manage Views</span>
                    </BaseButton>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Header>

        <Content
          style={{
            margin: '0',
            overflow: 'initial',
            flex: '1 1 0%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SpaceStatusContent spaceStatus={spaceStatus} space={space} onNavigateHome={() => navigate(RoutePaths.Home)}>
            {space && (
              <div className="space-content-container relative flex min-h-0 flex-1 flex-col">
                {isAnalyticsRoute ? (
                  <Suspense fallback={null}>
                    <Analytics spaceId={space.id} mcpUrl={MCP_URL} />
                  </Suspense>
                ) : (
                  <ViewContext.Provider
                    value={{
                      view: selectedView,
                      setView: setSelectedView,
                      views: views,
                      setViews: setViews,
                      isViewDirty,
                      setViewDirty: setIsViewDirty,
                    }}
                  >
                    {activeView &&
                      activeView.group !== WorkspaceViewGroup.planning &&
                      (activeView.representation === WorkspaceViewRepresentation.table ? (
                        <Backlog
                          activeView={activeView}
                          headerControls={true}
                          handleEditEstimationSettings={() => {
                            setSelectedTab(SpaceSettingsTab.PROPERTIES);
                            setOpenSpaceSettings(true);
                          }}
                          onImportClick={() => {
                            setSelectedTab(SpaceSettingsTab.IMPORT_TASKS);
                            setOpenSpaceSettings(true);
                          }}
                        />
                      ) : (
                        <Board
                          activeView={activeView}
                          framework={WorkspaceFramework.kanban}
                          onImportClick={() => {
                            setSelectedTab(SpaceSettingsTab.IMPORT_TASKS);
                            setOpenSpaceSettings(true);
                          }}
                        />
                      ))}
                    {activeView?.group === WorkspaceViewGroup.planning && (
                      <PlanningSession
                        activeView={activeView}
                        handleEditEstimationSettings={() => {
                          setSelectedTab(SpaceSettingsTab.PROPERTIES);
                          setOpenSpaceSettings(true);
                        }}
                      />
                    )}
                  </ViewContext.Provider>
                )}
              </div>
            )}
          </SpaceStatusContent>
        </Content>

        {space && (
          <NewSpaceViewModal
            open={isNewViewOpen}
            onOk={handleCreateNewView}
            onCancel={() => setIsNewViewOpen(false)}
            views={views}
          />
        )}

        {space && views && views.length && (
          <ManageViewsModal
            open={isManageViewsOpen}
            onOk={() => {}}
            onCancel={() => setIsManageViewsOpen(false)}
            view={selectedView}
            setView={setSelectedView}
            views={views.map((v) => {
              if (v.id === selectedView?.id) {
                return selectedView;
              }
              return v;
            })}
            setViews={setViews}
          />
        )}

        <div key={spaceId}>
          {openEditSpaceMembers && (
            <SpaceMembersModal isOpen={openEditSpaceMembers} onCancel={() => setOpenSpaceMembers(false)} />
          )}
        </div>
      </div>
      {spaceSettingsModals}
    </>
  );
}
